import { useEffect, useState } from 'react';
import { Search, Trash2 } from 'lucide-react';
import { RegistrationRecord, RegistrationStatus, RegistrationType } from '@/types';
import { registrationApi } from '@/services/api';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';

const statusOptions: Array<RegistrationStatus | 'all'> = ['all', 'New', 'Contacted', 'Converted', 'Closed'];
const typeOptions: Array<RegistrationType | 'all'> = ['all', 'course', 'sports', 'event'];

const statusSelectStyles: Record<RegistrationStatus, string> = {
  New: 'border-sky-200 bg-sky-50 text-sky-800 focus:border-sky-300 focus:ring-sky-500/15',
  Contacted: 'border-amber-200 bg-amber-50 text-amber-800 focus:border-amber-300 focus:ring-amber-500/15',
  Converted: 'border-emerald-200 bg-emerald-50 text-emerald-800 focus:border-emerald-300 focus:ring-emerald-500/15',
  Closed: 'border-slate-300 bg-slate-100 text-slate-700 focus:border-slate-400 focus:ring-slate-400/15',
};

const statusIconStyles: Record<RegistrationStatus, string> = {
  New: 'border-sky-200 bg-white/80 text-sky-700',
  Contacted: 'border-amber-200 bg-white/80 text-amber-700',
  Converted: 'border-emerald-200 bg-white/80 text-emerald-700',
  Closed: 'border-slate-300 bg-white/80 text-slate-600',
};

export const AdminUsersPage = () => {
  const [records, setRecords] = useState<RegistrationRecord[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<(typeof statusOptions)[number]>('all');
  const [itemType, setItemType] = useState<(typeof typeOptions)[number]>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    const timer = window.setTimeout(async () => {
      try {
        setIsLoading(true);
        const data = await registrationApi.getAll({
          search,
          status,
          itemType,
          signal: controller.signal,
        });
        setRecords(data);
        setError('');
      } catch (fetchError) {
        if ((fetchError as Error).name !== 'AbortError') {
          setError((fetchError as Error).message || 'Failed to fetch registrations.');
        }
      } finally {
        setIsLoading(false);
      }
    }, 200);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [itemType, search, status]);

  const handleStatusChange = async (id: string, nextStatus: RegistrationStatus) => {
    try {
      const updated = await registrationApi.updateStatus(id, nextStatus);
      setRecords((prev) => prev.map((record) => (record.id === id ? updated : record)));
    } catch (updateError) {
      console.error('Failed to update status:', updateError);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this registration record?')) {
      return;
    }

    try {
      await registrationApi.delete(id);
      setRecords((prev) => prev.filter((record) => record.id !== id));
    } catch (deleteError) {
      console.error('Failed to delete record:', deleteError);
    }
  };

  const formatDate = (value?: string | null) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  return (
    <div className="space-y-6">
      <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
        <div className="grid gap-3 lg:grid-cols-[340px_128px_128px] lg:justify-start">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by user, email, phone, or item"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="min-h-10 px-3 py-2 pl-10 text-sm"
            />
          </div>

          <Select
            wrapperClassName="rounded-[0.95rem] border border-slate-200/80 bg-slate-50/80 p-0.5 shadow-inner shadow-white/70"
            className="min-h-9 border-transparent bg-transparent px-3 py-2 text-xs font-semibold shadow-none focus:bg-white"
            iconClassName="h-6 w-6 border-white/80 bg-white text-slate-500"
            value={itemType}
            onChange={(event) => setItemType(event.target.value as (typeof typeOptions)[number])}
          >
              <option value="all">All Types</option>
              <option value="course">Course</option>
              <option value="sports">Sports</option>
              <option value="event">Event</option>
          </Select>

          <Select
            wrapperClassName="rounded-[0.95rem] border border-slate-200/80 bg-slate-50/80 p-0.5 shadow-inner shadow-white/70"
            className="min-h-9 border-transparent bg-transparent px-3 py-2 text-xs font-semibold shadow-none focus:bg-white"
            iconClassName="h-6 w-6 border-white/80 bg-white text-slate-500"
            value={status}
            onChange={(event) => setStatus(event.target.value as (typeof statusOptions)[number])}
          >
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {option === 'all' ? 'All Statuses' : option}
                </option>
              ))}
          </Select>
        </div>
      </Card>

      {isLoading ? (
        <Card className="rounded-[1.75rem] p-8">
          <div className="h-8 animate-pulse rounded-xl bg-slate-100" />
        </Card>
      ) : error ? (
        <Card className="rounded-[1.75rem] p-12 text-center">
          <p className="text-slate-600">{error}</p>
        </Card>
      ) : records.length === 0 ? (
        <Card className="rounded-[1.75rem] p-12 text-center">
          <p className="text-slate-600">No registrations found.</p>
        </Card>
      ) : (
        <div className="table-shell overflow-x-auto">
          <table className="data-table min-w-[1080px]">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">User Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Phone Number</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Email Address</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Registered Item</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Submitted</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/70">
                  <td className="px-4 py-3 font-semibold text-slate-950">{record.name}</td>
                  <td className="px-4 py-3 text-slate-600">{record.phone}</td>
                  <td className="px-4 py-3 text-slate-600">{record.email || '-'}</td>
                  <td className="px-4 py-3 text-slate-600">{record.itemName}</td>
                  <td className="px-4 py-3 text-slate-600">{record.itemTypeLabel}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">{formatDate(record.submittedAt || record.createdAt)}</td>
                  <td className="px-4 py-3">
                    <Select
                      wrapperClassName="max-w-[136px]"
                      className={statusSelectStyles[record.status] + ' min-h-9 rounded-full px-3 py-1.5 text-xs font-semibold shadow-none'}
                      iconClassName={statusIconStyles[record.status] + ' h-7 w-7'}
                      value={record.status}
                      onChange={(event) => handleStatusChange(record.id, event.target.value as RegistrationStatus)}
                    >
                      {statusOptions
                        .filter((option) => option !== 'all')
                        .map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                    </Select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end">
                      <Button variant="danger" size="sm" onClick={() => handleDelete(record.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
